import { useState, useMemo } from 'react'
import {
  getSuppliers, saveSupplier, deleteSupplier,
  getSupplierPrices, saveSupplierPrice
} from '../../lib/posStore'
import { useDepartment } from '../../context/DepartmentContext'
import {
  Plus, Pencil, Trash2, Search, Phone, Mail, MapPin,
  Tag, DollarSign, TrendingUp, X
} from 'lucide-react'

const CATEGORIES = ['Meat', 'Bakery', 'Groceries', 'Beverages', 'Packaging']

const categoryColors = {
  Meat: 'bg-red-900/40 text-red-300',
  Bakery: 'bg-amber-900/40 text-amber-300',
  Groceries: 'bg-green-900/40 text-green-300',
  Beverages: 'bg-blue-900/40 text-blue-300',
  Packaging: 'bg-purple-900/40 text-purple-300',
}

const emptySupplier = {
  name: '', contactPerson: '', phone: '', email: '',
  address: '', category: 'Groceries', paymentTerms: '', notes: ''
}

const emptyPrice = {
  ingredient: '', unit: '', price: ''
}

export default function Suppliers() {
  const { currentDepartmentId } = useDepartment()
  const [tab, setTab] = useState('suppliers')
  const [search, setSearch] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState({ ...emptySupplier })
  const [editingId, setEditingId] = useState(null)
  const [confirmDelete, setConfirmDelete] = useState(null)

  // Price list state
  const [selectedSupplierId, setSelectedSupplierId] = useState('')
  const [showPriceForm, setShowPriceForm] = useState(false)
  const [priceForm, setPriceForm] = useState({ ...emptyPrice })
  const [editingPriceId, setEditingPriceId] = useState(null)

  const suppliers = useMemo(() => getSuppliers(currentDepartmentId), [currentDepartmentId, showForm, confirmDelete, tab])

  const filteredSuppliers = useMemo(() => {
    if (!search.trim()) return suppliers
    const q = search.toLowerCase()
    return suppliers.filter(s =>
      s.name?.toLowerCase().includes(q) ||
      s.contactPerson?.toLowerCase().includes(q) ||
      s.category?.toLowerCase().includes(q)
    )
  }, [suppliers, search])

  const supplierPrices = useMemo(() => {
    if (!selectedSupplierId) return []
    return getSupplierPrices(selectedSupplierId)
  }, [selectedSupplierId, showPriceForm, tab])

  // Group prices by ingredient to show history
  const pricesByIngredient = useMemo(() => {
    const map = {}
    for (const p of supplierPrices) {
      const key = p.ingredient || 'Unknown'
      if (!map[key]) map[key] = []
      map[key].push(p)
    }
    // Sort each group by date descending
    for (const key of Object.keys(map)) {
      map[key].sort((a, b) => (b.updatedAt || '').localeCompare(a.updatedAt || ''))
    }
    return map
  }, [supplierPrices])

  // --- Supplier CRUD ---
  function openAdd() {
    setForm({ ...emptySupplier })
    setEditingId(null)
    setShowForm(true)
  }

  function openEdit(supplier) {
    setForm({ ...supplier })
    setEditingId(supplier.id)
    setShowForm(true)
  }

  function handleSave() {
    if (!form.name.trim()) return
    const record = editingId ? { ...form, id: editingId, departmentId: currentDepartmentId } : { ...form, departmentId: currentDepartmentId }
    saveSupplier(record)
    setShowForm(false)
    setForm({ ...emptySupplier })
    setEditingId(null)
  }

  function handleDelete(id) {
    deleteSupplier(id)
    setConfirmDelete(null)
  }

  // --- Price CRUD ---
  function openAddPrice() {
    setPriceForm({ ...emptyPrice })
    setEditingPriceId(null)
    setShowPriceForm(true)
  }

  function openEditPrice(price) {
    setPriceForm({ ingredient: price.ingredient, unit: price.unit, price: price.price })
    setEditingPriceId(price.id)
    setShowPriceForm(true)
  }

  function handleSavePrice() {
    if (!priceForm.ingredient.trim() || !priceForm.price) return
    const record = {
      ...(editingPriceId ? { id: editingPriceId } : {}),
      supplierId: selectedSupplierId,
      ingredient: priceForm.ingredient,
      unit: priceForm.unit,
      price: Number(priceForm.price),
    }
    saveSupplierPrice(record)
    setShowPriceForm(false)
    setPriceForm({ ...emptyPrice })
    setEditingPriceId(null)
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white p-4 sm:p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold">Supplier Management</h1>
            <p className="text-gray-400 text-sm mt-1">{suppliers.length} supplier{suppliers.length !== 1 ? 's' : ''} registered</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-gray-900 border border-gray-800 rounded-lg p-1 w-fit">
          {['suppliers', 'prices'].map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 text-sm font-medium rounded-md transition ${
                tab === t ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              {t === 'suppliers' ? 'Suppliers' : 'Price Lists'}
            </button>
          ))}
        </div>

        {/* ===== TAB 1: SUPPLIERS ===== */}
        {tab === 'suppliers' && (
          <>
            {/* Search & Add */}
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <div className="relative flex-1">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  type="text"
                  placeholder="Search suppliers..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="w-full bg-gray-900 border border-gray-800 rounded-lg pl-9 pr-4 py-2.5 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:border-gray-600"
                />
              </div>
              <button
                onClick={openAdd}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-4 py-2.5 rounded-lg text-sm font-medium transition"
              >
                <Plus size={16} /> Add Supplier
              </button>
            </div>

            {/* Supplier Cards Grid */}
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {filteredSuppliers.map(s => (
                <div key={s.id} className="bg-gray-900 border border-gray-800 rounded-lg p-5 relative group">
                  {/* Actions */}
                  <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition">
                    <button onClick={() => openEdit(s)} className="p-1.5 rounded bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white transition">
                      <Pencil size={14} />
                    </button>
                    <button onClick={() => setConfirmDelete(s.id)} className="p-1.5 rounded bg-gray-800 hover:bg-red-900/60 text-gray-400 hover:text-red-400 transition">
                      <Trash2 size={14} />
                    </button>
                  </div>

                  {/* Name & Category */}
                  <h3 className="font-semibold text-lg pr-16">{s.name}</h3>
                  {s.category && (
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs mt-2 ${categoryColors[s.category] || 'bg-gray-800 text-gray-300'}`}>
                      <Tag size={10} /> {s.category}
                    </span>
                  )}

                  {/* Details */}
                  <div className="mt-4 space-y-2 text-sm">
                    {s.contactPerson && (
                      <p className="text-gray-300">{s.contactPerson}</p>
                    )}
                    {s.phone && (
                      <div className="flex items-center gap-2 text-gray-400">
                        <Phone size={13} /> {s.phone}
                      </div>
                    )}
                    {s.email && (
                      <div className="flex items-center gap-2 text-gray-400">
                        <Mail size={13} /> {s.email}
                      </div>
                    )}
                    {s.address && (
                      <div className="flex items-center gap-2 text-gray-400">
                        <MapPin size={13} /> <span className="truncate">{s.address}</span>
                      </div>
                    )}
                  </div>

                  {s.paymentTerms && (
                    <p className="text-xs text-gray-500 mt-3 border-t border-gray-800 pt-2">
                      Payment: {s.paymentTerms}
                    </p>
                  )}

                  {/* Delete confirmation */}
                  {confirmDelete === s.id && (
                    <div className="absolute inset-0 bg-gray-900/95 rounded-lg flex flex-col items-center justify-center gap-3 p-4">
                      <p className="text-sm text-center">Delete <strong>{s.name}</strong>?</p>
                      <div className="flex gap-2">
                        <button onClick={() => handleDelete(s.id)} className="px-3 py-1.5 bg-red-600 hover:bg-red-500 text-white text-xs rounded transition">
                          Delete
                        </button>
                        <button onClick={() => setConfirmDelete(null)} className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-white text-xs rounded transition">
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {filteredSuppliers.length === 0 && (
              <div className="text-center py-16 text-gray-500">
                <p className="text-lg">No suppliers found</p>
                <p className="text-sm mt-1">Add your first supplier to get started</p>
              </div>
            )}

            {/* Supplier Form Modal */}
            {showForm && (
              <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
                <div className="bg-gray-900 border border-gray-800 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                  <div className="flex items-center justify-between p-5 border-b border-gray-800">
                    <h2 className="text-lg font-semibold">{editingId ? 'Edit Supplier' : 'New Supplier'}</h2>
                    <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-white"><X size={20} /></button>
                  </div>
                  <div className="p-5 space-y-4">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Supplier Name *</label>
                      <input
                        type="text"
                        value={form.name}
                        onChange={e => setForm({ ...form, name: e.target.value })}
                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-gray-500"
                        placeholder="e.g. Fresh Meats Co."
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Contact Person</label>
                      <input
                        type="text"
                        value={form.contactPerson}
                        onChange={e => setForm({ ...form, contactPerson: e.target.value })}
                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-gray-500"
                        placeholder="Full name"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Phone</label>
                        <input
                          type="tel"
                          value={form.phone}
                          onChange={e => setForm({ ...form, phone: e.target.value })}
                          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-gray-500"
                          placeholder="012 345 6789"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Email</label>
                        <input
                          type="email"
                          value={form.email}
                          onChange={e => setForm({ ...form, email: e.target.value })}
                          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-gray-500"
                          placeholder="supplier@email.com"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Address</label>
                      <input
                        type="text"
                        value={form.address}
                        onChange={e => setForm({ ...form, address: e.target.value })}
                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-gray-500"
                        placeholder="Full address"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Category</label>
                        <select
                          value={form.category}
                          onChange={e => setForm({ ...form, category: e.target.value })}
                          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-gray-500"
                        >
                          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Payment Terms</label>
                        <input
                          type="text"
                          value={form.paymentTerms}
                          onChange={e => setForm({ ...form, paymentTerms: e.target.value })}
                          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-gray-500"
                          placeholder="e.g. COD, 30 days"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Notes</label>
                      <textarea
                        value={form.notes}
                        onChange={e => setForm({ ...form, notes: e.target.value })}
                        rows={3}
                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-gray-500 resize-none"
                        placeholder="Additional notes..."
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-3 p-5 border-t border-gray-800">
                    <button onClick={() => setShowForm(false)} className="px-4 py-2 text-sm text-gray-400 hover:text-white transition">
                      Cancel
                    </button>
                    <button onClick={handleSave} className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium rounded-lg transition">
                      {editingId ? 'Update Supplier' : 'Add Supplier'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* ===== TAB 2: PRICE LISTS ===== */}
        {tab === 'prices' && (
          <>
            {/* Supplier Selector */}
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <div className="flex-1">
                <label className="block text-xs text-gray-400 mb-1">Select Supplier</label>
                <select
                  value={selectedSupplierId}
                  onChange={e => setSelectedSupplierId(e.target.value)}
                  className="w-full bg-gray-900 border border-gray-800 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-gray-600"
                >
                  <option value="">-- Choose a supplier --</option>
                  {suppliers.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
              {selectedSupplierId && (
                <div className="flex items-end">
                  <button
                    onClick={openAddPrice}
                    className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-4 py-2.5 rounded-lg text-sm font-medium transition"
                  >
                    <Plus size={16} /> Add Price
                  </button>
                </div>
              )}
            </div>

            {!selectedSupplierId ? (
              <div className="text-center py-16 text-gray-500">
                <DollarSign size={40} className="mx-auto mb-3 opacity-40" />
                <p className="text-lg">Select a supplier to view their price list</p>
              </div>
            ) : Object.keys(pricesByIngredient).length === 0 ? (
              <div className="text-center py-16 text-gray-500">
                <p className="text-lg">No prices recorded yet</p>
                <p className="text-sm mt-1">Add your first price entry for this supplier</p>
              </div>
            ) : (
              <div className="bg-gray-900 border border-gray-800 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-800">
                      <th className="text-left text-gray-400 font-medium px-4 py-3">Ingredient / Product</th>
                      <th className="text-left text-gray-400 font-medium px-4 py-3">Unit</th>
                      <th className="text-right text-gray-400 font-medium px-4 py-3">Current Price</th>
                      <th className="text-right text-gray-400 font-medium px-4 py-3">Last Updated</th>
                      <th className="text-right text-gray-400 font-medium px-4 py-3 w-20">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(pricesByIngredient).map(([ingredient, prices]) => {
                      const current = prices[0]
                      const prevPrice = prices.length > 1 ? prices[1].price : null
                      const priceDiff = prevPrice !== null ? ((current.price - prevPrice) / prevPrice * 100) : null

                      return (
                        <tr key={ingredient} className="border-b border-gray-800/50 hover:bg-gray-800/30 group">
                          <td className="px-4 py-3">
                            <div>
                              <span className="text-white font-medium">{ingredient}</span>
                              {/* Price history - last 5 */}
                              {prices.length > 1 && (
                                <div className="flex items-center gap-1 mt-1">
                                  <TrendingUp size={10} className="text-gray-500" />
                                  <span className="text-[11px] text-gray-500">
                                    History: {prices.slice(0, 5).map(p => `R${Number(p.price).toFixed(2)}`).join(' > ')}
                                  </span>
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-gray-300">{current.unit || '-'}</td>
                          <td className="px-4 py-3 text-right">
                            <span className="text-white font-semibold">R{Number(current.price).toFixed(2)}</span>
                            {priceDiff !== null && (
                              <span className={`ml-2 text-xs ${priceDiff > 0 ? 'text-red-400' : priceDiff < 0 ? 'text-green-400' : 'text-gray-500'}`}>
                                {priceDiff > 0 ? '+' : ''}{priceDiff.toFixed(1)}%
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-400 text-xs">
                            {current.updatedAt ? new Date(current.updatedAt).toLocaleDateString() : '-'}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button
                              onClick={() => openEditPrice(current)}
                              className="p-1.5 rounded bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white transition opacity-0 group-hover:opacity-100"
                            >
                              <Pencil size={13} />
                            </button>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Price Form Modal */}
            {showPriceForm && (
              <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setShowPriceForm(false)}>
                <div className="bg-gray-900 border border-gray-800 rounded-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
                  <div className="flex items-center justify-between p-5 border-b border-gray-800">
                    <h2 className="text-lg font-semibold">{editingPriceId ? 'Update Price' : 'Add Price Entry'}</h2>
                    <button onClick={() => setShowPriceForm(false)} className="text-gray-400 hover:text-white"><X size={20} /></button>
                  </div>
                  <div className="p-5 space-y-4">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Ingredient / Product *</label>
                      <input
                        type="text"
                        value={priceForm.ingredient}
                        onChange={e => setPriceForm({ ...priceForm, ingredient: e.target.value })}
                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-gray-500"
                        placeholder="e.g. Chicken breast"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Unit</label>
                        <input
                          type="text"
                          value={priceForm.unit}
                          onChange={e => setPriceForm({ ...priceForm, unit: e.target.value })}
                          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-gray-500"
                          placeholder="e.g. kg, L, each"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Price (R) *</label>
                        <input
                          type="number"
                          step="0.01"
                          value={priceForm.price}
                          onChange={e => setPriceForm({ ...priceForm, price: e.target.value })}
                          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-gray-500"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-end gap-3 p-5 border-t border-gray-800">
                    <button onClick={() => setShowPriceForm(false)} className="px-4 py-2 text-sm text-gray-400 hover:text-white transition">
                      Cancel
                    </button>
                    <button onClick={handleSavePrice} className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium rounded-lg transition">
                      {editingPriceId ? 'Update Price' : 'Add Price'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
